import {
  Corner,
  Root,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  Viewport,
} from '@radix-ui/react-scroll-area';
import * as React from 'react';
import { twMerge } from 'tailwind-merge';

interface ScrollAreaProps extends React.ComponentProps<typeof Root> {
  orientation?: 'horizontal' | 'vertical';
  withoutScrollBarPadding?: boolean;
}

export function ScrollArea({
  className,
  children,
  orientation = 'vertical',
  withoutScrollBarPadding = false,
  ...props
}: ScrollAreaProps): React.ReactElement {
  return (
    <Root data-slot="scroll-area" className={'relative overflow-hidden'} {...props}>
      <Viewport
        data-slot="scroll-area-viewport"
        className={twMerge(
          'focus-visible:ring-ring/50 size-full rounded-[inherit] transition-[color,box-shadow]',
          'outline-none focus-visible:ring-[3px] focus-visible:outline-1',
          withoutScrollBarPadding ? '' : orientation === 'vertical' ? 'pr-2.5' : 'pb-1',
          className,
        )}
      >
        {children}
      </Viewport>
      <ScrollBar orientation={orientation} />
      <Corner />
    </Root>
  );
}

function ScrollBar({
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<typeof ScrollAreaScrollbar>) {
  return (
    <ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      data-orientation={orientation}
      orientation={orientation}
      className={twMerge(
        'flex touch-none p-px transition-colors select-none',
        'data-[orientation=horizontal]:h-2.5 data-[orientation=horizontal]:flex-col data-[orientation=horizontal]:border-t',
        'data-[orientation=horizontal]:border-t-transparent',
        'data-[orientation=vertical]:h-full data-[orientation=vertical]:w-2.5',
        'data-[orientation=vertical]:border-l data-[orientation=vertical]:border-l-transparent',
        className,
      )}
      {...props}
    >
      <ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className="rounded-full bg-gray-200 relative flex-1 hover:bg-gray-300"
      />
    </ScrollAreaScrollbar>
  );
}
